import { supabase } from './supabase'

export type OperationalConfigKey = 'tarifas' | 'negociacao'

export type OperationalConfigRow = {
  id: number
  config_key: OperationalConfigKey
  effective_from: string
  title: string
  payload: Record<string, unknown>
  release_id: number | null
}

export type TariffBand = { from: number; to: number | null; water: number; sewer: number }
export type TariffCategoryConfig = { label: string; fixed_fee: number; bands: TariffBand[] }
export type TariffPayload = {
  decreto?: string
  subtotal_mode?: 'truncate_2' | 'round_2'
  categories: Record<'social' | 'residencial' | 'comercial', TariffCategoryConfig>
}
export type NegotiationBand = {
  min_days: number
  max_days: number | null
  cash_discount: number
  installment_discount: number
  max_installments: number
  label: string
}
export type NegotiationPayload = { bands: NegotiationBand[] }

const CACHE_PREFIX = 'suite.v8.operational-config.'

function readCache(key: OperationalConfigKey): OperationalConfigRow[] {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${key}`)
    return raw ? JSON.parse(raw) as OperationalConfigRow[] : []
  } catch {
    return []
  }
}

function writeCache(key: OperationalConfigKey, rows: OperationalConfigRow[]) {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(rows))
  } catch {
    // Cache é contingência; não bloqueia o cálculo.
  }
}

export async function loadOperationalConfigs(key: OperationalConfigKey) {
  const result = await supabase
    .from('operational_configs')
    .select('id,config_key,effective_from,title,payload,release_id')
    .eq('config_key', key)
    .order('effective_from', { ascending: true })

  if (!result.error && result.data) {
    const rows = result.data as OperationalConfigRow[]
    writeCache(key, rows)
    return { rows, source: 'online' as const }
  }

  const cached = readCache(key)
  if (cached.length > 0) return { rows: cached, source: 'cache' as const }
  throw result.error ?? new Error(`Configuração ${key} indisponível e sem cache local.`)
}

export async function loadOperationalConfigForDate(key: OperationalConfigKey, referenceDate: string) {
  const loaded = await loadOperationalConfigs(key)
  const config = loaded.rows
    .filter((row) => row.effective_from <= referenceDate)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null
  if (!config) throw new Error(`Não existe configuração de ${key} vigente em ${referenceDate}.`)
  return { config, source: loaded.source }
}

export function monthEndIso(monthValue: string) {
  const match = /^(\d{4})-(\d{2})$/.exec(monthValue)
  if (!match) return ''
  const year = Number(match[1])
  const month = Number(match[2])
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

export function currentMonthValue() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

const round2 = (value: number) => Number(value.toFixed(2))
const truncate2 = (value: number) => Math.trunc((value + Number.EPSILON) * 100) / 100

export type EconomyComposition = { social: number; residencial: number; comercial: number }

export type DynamicTariffResult = {
  configTitle: string
  effectiveFrom: string
  decreto?: string
  source: 'online' | 'cache'
  totalEconomies: number
  consumptionTotal: number
  consumptionPerEconomy: number
  water: number
  sewer: number
  fixed: number
  total: number
  details: Array<{
    category: keyof EconomyComposition
    categoryLabel: string
    economies: number
    band: number
    from: number
    to: number | null
    usedPerEconomy: number
    usedConnection: number
    waterRate: number
    sewerRate: number
    waterSubtotal: number
    sewerSubtotal: number
  }>
}

export function calculateTariffFromConfig(config: OperationalConfigRow, source: 'online' | 'cache', consumptionTotal: number, composition: EconomyComposition): DynamicTariffResult {
  if (!Number.isFinite(consumptionTotal) || consumptionTotal < 0) throw new Error('Informe um consumo válido.')
  const totalEconomies = composition.social + composition.residencial + composition.comercial
  if (!Number.isInteger(totalEconomies) || totalEconomies <= 0) throw new Error('Informe pelo menos uma economia.')
  if (Object.values(composition).some((value) => !Number.isInteger(value) || value < 0)) throw new Error('As quantidades de economias devem ser números inteiros maiores ou iguais a zero.')

  const payload = config.payload as unknown as TariffPayload
  if (!payload?.categories) throw new Error('A tabela tarifária publicada está incompleta.')
  const consumptionPerEconomy = consumptionTotal / totalEconomies
  const normalize = (payload.subtotal_mode ?? 'truncate_2') === 'round_2' ? round2 : truncate2
  let water = 0
  let sewer = 0
  let fixed = 0
  const details: DynamicTariffResult['details'] = []

  ;(['social', 'residencial', 'comercial'] as const).forEach((category) => {
    const economies = composition[category]
    if (economies <= 0) return
    const categoryConfig = payload.categories[category]
    if (!categoryConfig) throw new Error(`Categoria ${category} não configurada para esta vigência.`)
    fixed += round2(Number(categoryConfig.fixed_fee) * economies)

    categoryConfig.bands.forEach((band, index) => {
      const from = Number(band.from)
      const to = band.to == null ? null : Number(band.to)
      const upper = to == null ? consumptionPerEconomy : Math.min(consumptionPerEconomy, to)
      const usedPerEconomy = Math.max(0, upper - from)
      if (usedPerEconomy <= 0) return
      const waterSubtotal = normalize(usedPerEconomy * Number(band.water) * economies)
      const sewerSubtotal = normalize(usedPerEconomy * Number(band.sewer) * economies)
      water += waterSubtotal
      sewer += sewerSubtotal
      details.push({ category, categoryLabel: categoryConfig.label, economies, band: index + 1, from, to, usedPerEconomy, usedConnection: usedPerEconomy * economies, waterRate: Number(band.water), sewerRate: Number(band.sewer), waterSubtotal, sewerSubtotal })
    })
  })

  water = round2(water)
  sewer = round2(sewer)
  fixed = round2(fixed)
  return { configTitle: config.title, effectiveFrom: config.effective_from, decreto: String(payload.decreto ?? '') || undefined, source, totalEconomies, consumptionTotal, consumptionPerEconomy, water, sewer, fixed, total: round2(water + sewer + fixed), details }
}

export type DynamicNegotiationResult = {
  configTitle: string
  effectiveFrom: string
  source: 'online' | 'cache'
  days: number
  bandLabel: string
  cashDiscount: number
  installmentDiscount: number
  maxInstallments: number
  cashSavings: number
  cashValue: number
  installmentSavings: number
  installmentValue: number
}

function diffDays(start: Date, end: Date) {
  const a = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
  const b = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  return Math.round((b - a) / 86_400_000)
}

export function calculateNegotiationFromConfig(config: OperationalConfigRow, source: 'online' | 'cache', debt: number, debtDate: Date, referenceDate: Date): DynamicNegotiationResult {
  if (!Number.isFinite(debt) || debt <= 0) throw new Error('A dívida precisa ser maior que zero.')
  const days = diffDays(debtDate, referenceDate)
  if (days < 0) throw new Error('A data de cálculo deve ser igual ou posterior à data da dívida.')
  const payload = config.payload as unknown as NegotiationPayload
  if (!Array.isArray(payload?.bands)) throw new Error('As regras de negociação publicadas estão incompletas.')
  const band = payload.bands.find((item) => days >= Number(item.min_days) && (item.max_days == null || days <= Number(item.max_days)))
  if (!band) throw new Error('Não existe faixa de negociação para a quantidade de dias informada.')

  const cashDiscount = Number(band.cash_discount)
  const installmentDiscount = Number(band.installment_discount)
  const cashSavings = debt * (cashDiscount / 100)
  const installmentSavings = debt * (installmentDiscount / 100)
  return { configTitle: config.title, effectiveFrom: config.effective_from, source, days, bandLabel: band.label, cashDiscount, installmentDiscount, maxInstallments: Number(band.max_installments), cashSavings: round2(cashSavings), cashValue: round2(debt - cashSavings), installmentSavings: round2(installmentSavings), installmentValue: round2(debt - installmentSavings) }
}
